import { useBNBBusdPrice } from 'hooks/useBUSDPrice'
import { CSSProperties, useMemo } from 'react'
import { useLiveGasPrice } from 'utils/liveGas'
import { useAccount, useBalance } from 'wagmi'
import { bnbNeed, FALLBACK_GAS_PRICE, GasAction, isNoGasError, isShort } from './logic'
import NoBnbPanel from './NoBnbPanel'

export { isNoGasError } from './logic'
export type { GasAction } from './logic'

const BSC = 56

/** The connected wallet's BNB against what the next `action` needs (gas estimate + any BNB the step sends). Reads only. */
export function useNoBnb(action: GasAction, extraWei: bigint = 0n) {
  const { address } = useAccount()
  const { data: bal } = useBalance({ address, chainId: BSC, enabled: Boolean(address), staleTime: 10_000, watch: true } as any)
  const gasPrice = useLiveGasPrice(BSC)
  const price = useBNBBusdPrice({ forceMainnet: true })
  const bnbUsd = useMemo(() => {
    const n = price ? Number(price.toSignificant(8)) : NaN
    return Number.isFinite(n) && n > 0 ? n : undefined
  }, [price])
  const need = bnbNeed({ action, gasPriceWei: gasPrice ?? FALLBACK_GAS_PRICE, extraWei })
  const haveWei = bal?.value as bigint | undefined
  return { address, haveWei, bnbUsd, ...need, short: Boolean(address) && isShort(haveWei, need.totalWei) }
}

/**
 * Drop-in guide for a transaction form: renders nothing while the wallet has enough BNB, and the "No BNB" panel when the read
 * balance is below the estimate, or when `error` is a wallet / node "insufficient funds" error caught during the transaction.
 * It never disables, changes or sends a transaction.
 */
export default function NoBnbGuide({ action, extraWei = 0n, extraLabel, error, testId, style }: { action: GasAction; extraWei?: bigint; extraLabel?: string; error?: unknown; testId?: string; style?: CSSProperties }) {
  const n = useNoBnb(action, extraWei)
  const caught = error !== undefined && error !== null && error !== '' && isNoGasError(error)
  if (!n.address || (!n.short && !caught)) return null
  return (
    <NoBnbPanel
      address={n.address}
      gasWei={n.gasWei}
      extraWei={n.extraWei}
      extraLabel={extraLabel}
      haveWei={n.haveWei}
      bnbUsd={n.bnbUsd}
      reason={caught ? 'error' : 'balance'}
      testId={testId}
      style={style}
    />
  )
}
